import React from "react";
import styles from "../styles/webStyles";
import { Grid } from "@material-ui/core";
import { Helmet } from "react-helmet";
import { useMediaQuery } from "react-responsive";
import PageTitle from "../components/Website/PageTitle";
import PageContainer from "../components/PageContainer";

const DataProtection = () => {
  const isPhone = useMediaQuery({ query: "(max-width: 812px)" });

  return (
    <PageContainer>
      <Helmet>
        <title>Data Protection | NTI</title>
      </Helmet>
      <PageTitle title="Data Protection Policy" />

      <div className="container">
        <Grid container>
          <Grid items xs={12}>
            <div style={{ margin: isPhone ? "3rem 1rem" : "5rem 3rem" }}>
              <h2
                style={{
                  ...styles.subHeader,
                  fontSize: isPhone ? "20px" : "30px",
                  fontWeight: 900,
                  margin: 0,
                }}
              >
                Data Protection Policy
              </h2>

              <p>
                The Accelerated Teacher Professionalization Pathway (ATPP),
                implemented by the National Teachers’ Institute (NTI), is
                committed to protecting personal data in compliance with the
                Nigeria Data Protection Act 2023 and regulatory guidance issued
                by the Nigerian Data Protection Commission.
              </p>
              <p>
                This policy defines how personal data is collected, processed,
                stored, secured, and governed across the ATPP LMS.
              </p>

              <h3>1. Introduction</h3>
              <p>
                This Data Protection Policy sets out governance and compliance
                expectations for the collection and processing of personal data
                across the ATPP LMS and related digital systems.
              </p>

              <h3>2. Scope</h3>
              <p>This policy applies to:</p>
              <ul>
                <li>All ATPP learners (teachers)</li>
                <li>NTI staff and facilitators</li>
                <li>Institutional partners (State Education Boards, Schools)</li>
                <li>Third-party service providers</li>
                <li>
                  All digital systems supporting ATPP (LMS, analytics, reporting
                  tools)
                </li>
              </ul>

              <h3>3. Key Definitions</h3>
              <ul>
                <li>
                  <strong>Personal Data:</strong> Any information relating to an
                  identified or identifiable individual
                </li>
                <li>
                  <strong>Data Subject:</strong> Any individual whose data is
                  processed
                </li>
                <li>
                  <strong>Processing:</strong> Collection, storage, use, sharing,
                  or deletion of data
                </li>
                <li>
                  <strong>Controller:</strong> NTI (determines purpose of
                  processing)
                </li>
                <li>
                  <strong>Processor:</strong> LMS providers and partners acting on
                  NTI’s behalf
                </li>
              </ul>

              <h3>4. Data Protection Principles</h3>
              <p>ATPP adheres to the following principles:</p>
              <ul>
                <li>Lawfulness, Fairness, Transparency</li>
                <li>Purpose Limitation</li>
                <li>Data Minimization</li>
                <li>Accuracy</li>
                <li>Storage Limitation</li>
                <li>Integrity and Confidentiality</li>
                <li>Accountability</li>
              </ul>

              <h3>5. Lawful Basis for Processing</h3>
              <p>Data is processed based on:</p>
              <ul>
                <li>Legal obligation (teacher training mandate)</li>
                <li>Public interest (education system strengthening)</li>
                <li>Consent (where required)</li>
                <li>Contractual necessity (programme participation)</li>
              </ul>

              <h3>6. Categories of Data Collected</h3>
              <p>We may collect:</p>
              <ul>
                <li>
                  Identification data (name, phone number, email, NIN where
                  applicable)
                </li>
                <li>Professional data (teaching qualifications, school details)</li>
                <li>Academic data (course progress, assessments, certifications)</li>
                <li>Technical data (login activity, device/browser info)</li>
                <li>Payment data (where applicable for paid modules)</li>
              </ul>

              <h3>7. Purpose of Data Processing</h3>
              <p>Data is processed to:</p>
              <ul>
                <li>Deliver ATPP training and certification</li>
                <li>Track learning progress and performance</li>
                <li>Generate institutional and national-level reports</li>
                <li>Improve programme quality and outcomes</li>
                <li>Ensure compliance with regulatory frameworks</li>
              </ul>

              <h3>8. Data Security Measures</h3>
              <p>ATPP implements:</p>
              <ul>
                <li>End-to-end encryption (in transit and at rest)</li>
                <li>Role-based access control (RBAC)</li>
                <li>Multi-factor authentication for admin users</li>
                <li>Secure cloud infrastructure</li>
                <li>API security controls</li>
                <li>Continuous monitoring and logging</li>
                <li>Periodic security audits and penetration testing</li>
              </ul>

              <h3>9. Data Retention Policy</h3>
              <p>
                Data is retained only as long as necessary for programme and
                regulatory purposes. Training records may be retained for
                certification verification and audits. Data is securely deleted
                or anonymized after retention periods expire.
              </p>

              <h3>10. Data Sharing and Transfers</h3>
              <p>Data may be shared with:</p>
              <ul>
                <li>Government education authorities</li>
                <li>Accredited partners</li>
                <li>Technology providers (under strict contracts)</li>
              </ul>
              <p>All third parties must:</p>
              <ul>
                <li>Sign Data Processing Agreements (DPAs)</li>
                <li>Meet NDPC compliance requirements</li>
              </ul>
              <p>
                Cross-border transfers (if any) will follow NDPC-approved
                safeguards.
              </p>

              <h3>11. Data Subject Rights</h3>
              <p>Data subjects have the right to:</p>
              <ul>
                <li>Access their data</li>
                <li>Rectify inaccurate data</li>
                <li>Request deletion (where applicable)</li>
                <li>Restrict or object to processing</li>
                <li>Data portability</li>
              </ul>
              <p>Requests will be handled within regulatory timelines.</p>

              <h3>12. Data Breach Management</h3>
              <p>In the event of a breach:</p>
              <ul>
                <li>Immediate containment and investigation</li>
                <li>Risk assessment</li>
                <li>
                  Notification to the Nigerian Data Protection Commission where
                  required
                </li>
                <li>Notification to affected users (if high risk)</li>
                <li>Corrective and preventive actions</li>
              </ul>

              <h3>13. Governance and Accountability</h3>
              <ul>
                <li>A Data Protection Officer (DPO) is designated</li>
                <li>Regular compliance audits are conducted</li>
                <li>Staff receive periodic data protection training</li>
                <li>Safeguarding and privacy are embedded in programme design</li>
              </ul>

              <h3>14. Policy Review</h3>
              <p>
                This policy is reviewed periodically in line with regulatory
                updates and operational changes.
              </p>
            </div>
          </Grid>
        </Grid>
      </div>
    </PageContainer>
  );
};

export default DataProtection;

